import { Droplet, Package, Pencil, Plus, Trash2, Truck, X } from 'lucide-react';
import {
	cn,
	dayRange,
	EmptyState,
	IconButton,
	Radio,
	rowsFor,
	scopeHint,
	Section,
} from './internal';
import type { SectionFrame } from './internal';
import {
	NO_SAMPLE,
	SAMPLE_NO_SHIPPING,
	SAMPLE_WITH_SHIPPING,
	type CurrencyTextComponent,
	type ExtrasProduct,
	type ExtrasSample,
} from './types';

/* Written out in full, both variants: Tailwind reads class names as literal
   strings, so a grid built by interpolation is a grid that never gets
   generated. */
const GRID = 'grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]';
const GRID_EDIT = 'grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_52px]';

/**
 * The three ways a sample can be taken. Worded once, here, so the offer card and
 * the accept flow's first step can't offer the client two different vocabularies
 * for the same decision.
 */
export const SAMPLE_OPTIONS: Array<{
	value: string;
	label: string;
	description: string;
	icon: React.ElementType;
}> = [
	{
		value: NO_SAMPLE,
		label: 'No Sample',
		description: 'Skip sample validation and proceed directly to production.',
		icon: X,
	},
	{
		value: SAMPLE_NO_SHIPPING,
		label: 'Sample with Video/Images',
		description: 'Receive sample validation through video and image documentation.',
		icon: Package,
	},
	{
		value: SAMPLE_WITH_SHIPPING,
		label: 'Sample with Physical Delivery',
		description: 'Receive the physical sample shipped to your address.',
		icon: Truck,
	},
];

export interface SamplesSectionProps {
	products: ExtrasProduct[];
	CurrencyText: CurrencyTextComponent;
	/**
	 * The three ways of taking the sample, priced. Rendered whenever given —
	 * admin sees what it is offering and for how much, the same way the client
	 * does, rather than a bare total with no sign that two cheaper options exist.
	 */
	choices?: {
		/** Keyed by offerId; an absent entry means NO_SAMPLE. Omit the map for a
		 * preview where nothing has been picked. */
		selected?: Record<string, string>;
		/** Omitted → the tiles are a priced preview of what the client chooses
		 * between, not a control. Admin authors the sample; it doesn't decide how
		 * the sample gets taken. */
		onSelect?: (offerId: string, choice: string) => void;
	};
	/** Authoring actions. Given → every product gets a row, including the ones
	 * with no sample: that row is where Add lives. */
	actions?: {
		onAdd: (offerId: string) => void;
		onEdit: (offerId: string, sample: ExtrasSample) => void;
		onRemove: (sampleId: string) => void;
	};
	/** Which page this is on — see `SectionFrame`. Defaults to the v1 panel. */
	frame?: SectionFrame;
}

export function SamplesSection({
	products,
	CurrencyText,
	choices,
	actions,
	frame,
}: SamplesSectionProps) {
	const editable = !!actions;
	const rows = rowsFor(products, (p) => !!p.sample, editable);
	const withSample = products.filter((p) => p.sample).length;
	const gridClass = editable ? GRID_EDIT : GRID;

	const priceFor = (sample: ExtrasSample, choice: string) =>
		sample.pricesByChoice?.find((p) => p.sampleChoice === choice)?.totalPrice ?? null;

	return (
		<Section
			icon={Droplet}
			title="Samples"
			subtitle={
				choices?.onSelect
					? 'choose how you approve before the full run'
					: 'what the client chooses between before the full run'
			}
			hint={scopeHint(withSample, products.length)}
			defaultOpen={withSample > 0}
			frame={frame}
		>
			{rows.length === 0 && (
				<EmptyState>
					No samples on this offer — the products go straight to the full run.
				</EmptyState>
			)}

			{rows.length > 0 && (
				<div className={`${gridClass} items-center gap-3 pb-1.5 text-2xs text-text-4`}>
					<div>Product</div>
					<div>Total</div>
					<div>Production</div>
					<div>Delivery</div>
					{editable && <div />}
				</div>
			)}

			{rows.map((product) => {
				const sample = product.sample;
				// The picked option, when one has been. A preview (admin) has none, so
				// the Total column falls back to the authored figure.
				const choice = choices?.selected?.[product.offerId] ?? null;
				// The total follows the option taken. Quoting the with-shipping figure
				// as "the" price and then charging less for video/images read as the
				// price changing under the client.
				//
				// Falls back to the authored total for a caller without per-choice
				// prices — `pricesByChoice` is on the client sample type only, so the
				// admin card can price the tiles' effect on the BATCH total (the
				// summary answers to the choices) but not yet each option's own.
				const total = sample
					? ((choice ? priceFor(sample, choice) : null) ?? sample.totalPrice)
					: null;
				// Authoring breakdown behind the total, one hover away. It was four
				// columns of its own, which cost the shared layout for a number only
				// the person writing the sample needs.
				const breakdownTitle = sample?.breakdown?.length
					? sample.breakdown.map((b) => `${b.label}: ${b.amount ?? '—'}`).join('\n')
					: undefined;

				return (
					<div
						key={product.offerId}
						className={`${gridClass} items-center gap-3 border-t border-line-1 py-2.5`}
					>
						<div className="flex min-w-0 flex-col gap-0.5 leading-[1.3]">
							{/* Muted rather than faded. Blanket opacity made the whole row hard
							    to read; with no accent left on it and a note underneath, a quieter
							    name is enough to say it isn't counting. */}
							<span
								className={cn(
									'truncate text-sm font-medium',
									product.excluded ? 'text-text-3' : 'text-text-1'
								)}
							>
								{product.name}
							</span>
							{/* The sample is still authored and still priced — the product
							    it belongs to just isn't being bought. */}
							{product.excluded && (
								<span className="truncate text-xs text-text-4">Not included</span>
							)}
							{/* CHARGED earns no note — it is the ordinary case, and marking
							    it would badge almost every sample. */}
							{sample?.treatmentNote && (
								<span className="truncate text-xs text-accent">
									{sample.treatmentNote}
								</span>
							)}
						</div>

						{!sample ? (
							<div className="col-span-3 text-sm text-text-4">No sample set.</div>
						) : (
							<>
								<span
									className={cn(breakdownTitle && 'w-fit cursor-help')}
									title={breakdownTitle}
									style={
										breakdownTitle
											? {
													textDecoration: 'underline dotted',
													textUnderlineOffset: '3px',
													textDecorationColor: 'var(--line-3)',
												}
											: undefined
									}
								>
									<CurrencyText
										amount={total}
										className="font-mono text-sm font-medium text-text-1"
									/>
								</span>
								<span className="text-sm text-text-3">
									{dayRange(sample.minProductionDays, sample.maxProductionDays)}
								</span>
								<span className="text-sm text-text-3">
									{dayRange(sample.deliveryDaysMin, sample.deliveryDaysMax)}
								</span>
							</>
						)}

						{actions && (
							<div className="flex items-center justify-end gap-1.5">
								{sample ? (
									<>
										<IconButton
											title="Edit sample"
											onClick={() => actions.onEdit(product.offerId, sample)}
										>
											<Pencil size={11} />
										</IconButton>
										<IconButton
											title="Remove sample"
											danger
											onClick={() => actions.onRemove(sample.id)}
										>
											<Trash2 size={11} />
										</IconButton>
									</>
								) : (
									<IconButton
										title="Add a sample to this product"
										onClick={() => actions.onAdd(product.offerId)}
									>
										<Plus size={12} />
									</IconButton>
								)}
							</div>
						)}

						{sample?.notes && (
							<div className="col-span-full text-xs text-text-4">{sample.notes}</div>
						)}

						{/* The choice itself, under the facts rather than in a column of
						    its own: three labelled options don't survive a table cell,
						    and the total and lead times above are what they're weighed
						    against. */}
						{sample && choices && (
							<div className="col-span-full flex flex-wrap gap-1.5 pt-1">
								{SAMPLE_OPTIONS.map((opt) => {
									// An excluded product has chosen nothing — nothing about it counts — so
									// its tiles carry no accent. Dimming alone wasn't enough: a 60%-opacity
									// accent border still reads as "selected", which is the one thing this
									// row must not say.
									const isSelected = !product.excluded && choice === opt.value;
									const Icon = opt.icon;
									return (
										<button
											key={opt.value}
											type="button"
											// A preview has no choice to make. Same tiles, same
											// prices, just not a control.
											disabled={!choices.onSelect || !!product.excluded}
											title={opt.description}
											onClick={() =>
												product.excluded
													? undefined
													: choices.onSelect?.(product.offerId, opt.value)
											}
											className={cn(
												'flex min-w-0 flex-[1_1_0] items-center gap-1.5 rounded-md border px-2.5 py-2 text-left',
												choices.onSelect && !product.excluded
													? 'cursor-pointer'
													: 'cursor-default'
											)}
											style={{
												borderColor: isSelected
													? 'var(--accent)'
													: 'var(--line-2)',
												background: isSelected
													? 'var(--accent-soft)'
													: 'transparent',
											}}
										>
											<Radio checked={isSelected} />
											<Icon size={12} className="shrink-0 text-text-3" />
											<span
												className={cn(
													'min-w-0 truncate text-sm font-medium',
													isSelected ? 'text-text-1' : 'text-text-2'
												)}
											>
												{opt.label}
											</span>
											<CurrencyText
												amount={priceFor(sample, opt.value)}
												className={cn(
													'ml-auto shrink-0 whitespace-nowrap font-mono text-xs',
													isSelected ? 'text-text-1' : 'text-text-3'
												)}
											/>
										</button>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</Section>
	);
}
